import ReactTooltip from 'react-tooltip';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSignupRedirectLinkAccountPlans, useHubSwitch, useUserSettings } from '@bpm-web-app/utils';
import { useGetMeSubscription } from '@bpm-web-app/swr-hooks';
import { SubscriptionDetailsv4 } from '@bpm-web-app/supreme-api-sdk';
import { useRouter } from 'next/router';
import FullscreenExit from '../../assets/icons/fullscreen-exit.svg';
import Fullscreen from '../../assets/icons/fullscreen.svg';
import UpgradePlan from '../../assets/icons/upgrade-plan.svg';
import Help from '../../assets/icons/help.svg';

import styles from './nav-footer.module.css';
import { useOnDragElement } from '../drag-and-drop/drag-and-drop.context';
import { PlainNavItem } from './plain-nav-item';

export function NavFooter() {
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const { isArtistPlatform, isCreate, isDownload } = useHubSwitch();
    const { isDragging } = useOnDragElement();
    const { isAnonymous } = useUserSettings();
    const router = useRouter();

    useEffect(() => {
        ReactTooltip.rebuild();
    }, []);

    useEffect(() => {
        const handler = () => {
            setIsFullscreen(!isFullscreen);
        };

        // This event requires specific prefixes to work in different browsers
        // https://meet.google.com/dsf-vbiv-naw?pli=1
        window.addEventListener('fullscreenchange', handler);
        window.addEventListener('mozfullscreenchange', handler);
        window.addEventListener('webkitfullscreenchange', handler);
        window.addEventListener('msfullscreenchange', handler);
        return () => {
            window.removeEventListener('fullscreenchange', handler);
            window.removeEventListener('mozfullscreenchange', handler);
            window.removeEventListener('webkitfullscreenchange', handler);
            window.removeEventListener('msfullscreenchange', handler);
        };
    }, [isFullscreen]);

    /* istanbul ignore next */
    const toggleFullscreen = useCallback(() => {
        /* Any references to webkit- prefixed properties/methods are here for Safari support. */
        if (!(document.fullscreenEnabled || document.webkitFullscreenEnabled)) return;

        if (document.fullscreenElement || document.webkitFullscreenElement) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            }
        } else {
            const docEl = document.documentElement;
            if (docEl.requestFullscreen) {
                docEl.requestFullscreen();
            } else if (docEl.webkitRequestFullscreen) {
                docEl.webkitRequestFullscreen();
            }
        }
    }, []);

    const { data } = useGetMeSubscription('supreme', !isDownload);
    const { data: createData } = useGetMeSubscription('create', !isCreate);

    const showUpgrade = useMemo(() => {
        if (isCreate) {
            return createData?.data.membership.subscription?.membership_type !== SubscriptionDetailsv4.MembershipTypeEnum.PremiumPlus;
        } if (!isCreate && !isArtistPlatform) {
            return data?.data.membership.subscription?.membership_type !== SubscriptionDetailsv4.MembershipTypeEnum.Premium;
        }
        return false;
    }, [createData, data, isCreate, isArtistPlatform]);

    return (
        <div className={styles['nav-footer']}>
            <PlainNavItem
                title={isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
                icon={isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                activeIcon={<FullscreenExit />}
                action={toggleFullscreen}
                isDragging={isDragging}
                disableDragDrop />
            {showUpgrade &&
                (
                    <PlainNavItem
                        title="Upgrade Plan"
                        icon={<UpgradePlan />}
                        activeIcon={<UpgradePlan />}
                        action={() => {
                            if (isAnonymous) {
                                router.replace(getSignupRedirectLinkAccountPlans());
                            } else {
                                router.push('/account/plan');
                            }
                        }}
                        isDragging={isDragging}
                        disableDragDrop />
                )
            }
            <PlainNavItem
                title="Help"
                icon={<Help />}
                activeIcon={<Help />}
                path="/account/support"
                isDragging={isDragging}
                disableDragDrop />
        </div>
    );
}
